import type { Input } from "../core/Input";
import { GameWindow, type Anchor } from "./Window";

export interface MenuItem<T = unknown> {
  label: string;
  value?: T;
  disabled?: boolean;
  /** Right-aligned trailing text, e.g. an MP cost or an item count. */
  detail?: string;
}

export interface MenuOptions<T> {
  title?: string;
  items: readonly MenuItem<T>[];
  anchor?: Anchor;
  cancelable?: boolean;
  /** Index the cursor starts on. */
  initial?: number;
  /** Fires whenever the cursor moves, including once on open. */
  onHighlight?: (index: number, item: MenuItem<T>) => void;
}

export interface MenuHooks {
  onMove?: () => void;
  onSelect?: () => void;
  onCancel?: () => void;
}

/** Cursor-driven vertical list. Wraps at both ends, like the games do. */
export class Menu {
  private readonly window: GameWindow;
  private items: readonly MenuItem<unknown>[] = [];
  private index = 0;
  private cancelable = true;
  private resolve: ((value: number | null) => void) | null = null;
  private onHighlight: ((index: number, item: MenuItem<never>) => void) | undefined;
  private repeatTimer = 0;

  constructor(parent: HTMLElement, private input: Input, private hooks: MenuHooks = {}) {
    this.window = new GameWindow(parent, "ds-menu");
  }

  get busy(): boolean {
    return this.resolve !== null;
  }

  get selectedIndex(): number {
    return this.index;
  }

  /** Opens the menu and resolves with the chosen index, or null if cancelled. */
  open<T>(options: MenuOptions<T>): Promise<number | null> {
    this.items = options.items as readonly MenuItem<unknown>[];
    this.index = Math.min(Math.max(options.initial ?? 0, 0), Math.max(0, this.items.length - 1));
    this.cancelable = options.cancelable ?? true;
    this.onHighlight = options.onHighlight as never;
    this.repeatTimer = 0;

    // Never park the cursor on a disabled row.
    if (this.items[this.index]?.disabled) this.step(1, false);

    this.window.anchor(options.anchor ?? {});
    this.render(options.title);
    this.window.show();
    this.emitHighlight();

    return new Promise<number | null>((resolve) => {
      this.resolve = resolve;
    });
  }

  close(): void {
    this.window.hide();
    const resolve = this.resolve;
    this.resolve = null;
    resolve?.(null);
  }

  update(dt: number): void {
    if (!this.resolve) return;

    // Tap to move one row, hold to scroll after a short delay.
    const down = this.input.pressed("down") || this.input.pressed("right");
    const up = this.input.pressed("up") || this.input.pressed("left");
    const holdingDown = this.input.held("down") || this.input.held("right");
    const holdingUp = this.input.held("up") || this.input.held("left");

    if (down) this.step(1);
    else if (up) this.step(-1);
    else if (holdingDown || holdingUp) {
      this.repeatTimer += dt;
      if (this.repeatTimer > 0.35) {
        this.repeatTimer = 0.28;
        this.step(holdingDown ? 1 : -1);
      }
    } else {
      this.repeatTimer = 0;
    }

    if (this.input.consume("confirm")) {
      const item = this.items[this.index];
      if (item && !item.disabled) {
        this.hooks.onSelect?.();
        this.finish(this.index);
      }
      return;
    }

    if (this.cancelable && (this.input.consume("cancel") || this.input.consume("menu"))) {
      this.hooks.onCancel?.();
      this.finish(null);
    }
  }

  private step(delta: number, sound = true): void {
    if (this.items.length === 0) return;
    const start = this.index;
    let next = this.index;
    for (let i = 0; i < this.items.length; i++) {
      next = (next + delta + this.items.length) % this.items.length;
      if (!this.items[next].disabled) break;
    }
    if (next === start) return;
    this.index = next;
    if (sound) this.hooks.onMove?.();
    this.updateCursor();
    this.emitHighlight();
  }

  private emitHighlight(): void {
    const item = this.items[this.index];
    if (item) this.onHighlight?.(this.index, item as MenuItem<never>);
  }

  private render(title?: string): void {
    const rows = this.items
      .map((item, i) => {
        const classes = ["ds-item"];
        if (i === this.index) classes.push("selected");
        if (item.disabled) classes.push("disabled");
        const detail = item.detail ? `<span class="ds-detail">  ${escapeHTML(item.detail)}</span>` : "";
        return `<div class="${classes.join(" ")}">${escapeHTML(item.label)}${detail}</div>`;
      })
      .join("");
    const heading = title ? `<div class="ds-title">${escapeHTML(title)}</div>` : "";
    this.window.setHTML(heading + rows);
  }

  private updateCursor(): void {
    const rows = this.window.el.querySelectorAll(".ds-item");
    rows.forEach((row, i) => row.classList.toggle("selected", i === this.index));
  }

  private finish(value: number | null): void {
    this.window.hide();
    const resolve = this.resolve;
    this.resolve = null;
    resolve?.(value);
  }

  destroy(): void {
    this.window.destroy();
  }
}

function escapeHTML(value: string): string {
  return value.replace(
    /[&<>"']/g,
    (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" })[c]!,
  );
}
